import { ref, computed, Ref, watch, reactive, isRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { PaginatedResponse, Patient } from '@/models';
import { useRouter } from 'vue-router';
import { IOption, ISuggestion } from '@/lib';
import { OrderItem, Variant, LensProperty, ProductImage, ChangeParams } from '@/models/webshop.model';
import { useWebshopCartStore } from '@/stores/webshopCart.store';
import { useNotificationStore } from '@/stores/notification.store';
import debounce from 'lodash-es/debounce';
import { formatPower } from '@/lib/helpers/format-power';
import { OrganisationPatientService, WebshopService } from '@/services/api';
import { PRODUCT_CATEGORY_LENS_CARE } from '@/constants/webshop';
import { formatVariantLabel, sortVariantsForPackOptions } from '@/lib/helpers/variant-label.helper';
import {
  getDisplayableLensProperties,
  convertPropertyToOptions,
  getOptionLabel,
  sortLensProperties,
  isValueValidForProperty
} from '@/lib/helpers/lens-property.helper';

export interface PropertyOption {
  value: string;
  label: string;
}

export function useProductCard(props: {
  mlsId: string;
  productName: string;
  productCategory: string;
  productCategorySlug: string;
  productCategoryDisplayName?: string;
  lensProperties: LensProperty[] | Ref<LensProperty[]>;
  variants: Variant[] | Ref<Variant[]>;
  image?: ProductImage;
  lensCode?: number;
  powerSelectionMode?: 'single' | 'multi';
  dataDusk?: string;
  powerSelectionRef?: Ref<({ reset: () => void } | null)>;
  organisationId: Ref<string>;
}) {
  const { t } = useI18n();
  const webshopCartStore = useWebshopCartStore();
  const notificationStore = useNotificationStore();
  const router = useRouter();
  const isLoadingParameters = ref(false);

  // Cache service instances to avoid recreation
  const webshopService = ref<WebshopService | null>(null);
  const patientService = ref<OrganisationPatientService | null>(null);

  // Initialize services and update when organization changes
  watch(
    () => props.organisationId.value,
    (orgId) => {
      if (orgId) {
        webshopService.value = new WebshopService(orgId);
        patientService.value = new OrganisationPatientService(orgId);
      }
    },
    { immediate: true }
  );

  const internalLensProperties = ref(
    isRef(props.lensProperties) ? props.lensProperties.value : props.lensProperties
  );

  const lensPropertiesSource = computed(() => internalLensProperties.value ?? []);

  const variants = ref(props.variants);

  // Dynamic parameter storage - stores all lens property values
  const dynamicParameters = reactive<Record<string, string | null>>({});

  const displayableProperties = computed(() => {
    const filtered = getDisplayableLensProperties(lensPropertiesSource.value);
    return sortLensProperties(filtered);
  });

  // Track which parameters were explicitly selected by the user (vs auto-filled by lensFitting)
  // Only user-selected parameters should be sent in current_params to MLS
  // Declared early because it's used by initializePropertyDefault which runs immediately
  const userSelectedParams = ref<Set<string>>(new Set());

  // Helper function to initialize a single property's default value
  const initializePropertyDefault = (property: LensProperty): void => {
    // Skip if user has explicitly selected this parameter (even if it's now invalid)
    if (userSelectedParams.value.has(property.name)) {
      return;
    }

    // Skip if already initialized with a value (check if key exists and has a non-null value)
    if (property.name in dynamicParameters && dynamicParameters[property.name] !== null) {
      return;
    }

    const options = convertPropertyToOptions(property, formatPower);

    // Auto-select if there's exactly one option
    if (options.length === 1) {
      dynamicParameters[property.name] = options[0].value;
    } else if (
      property.name === 'Material' &&
      typeof property.defaultOptionId === 'number' &&
      property.options &&
      property.options.length > 0
    ) {
      // Auto-select Material's defaultOptionId (MTO lenses have multiple materials)
      // Note: defaultOptionId can be 0, so we check typeof instead of truthiness
      // Use loose equality (==) to handle potential string/number type mismatches from API
      const defaultOption = property.options.find(
        (opt) => String(opt.id) === String(property.defaultOptionId)
      );
      if (defaultOption) {
        dynamicParameters[property.name] = String(defaultOption.optionValue);
      } else {
        // Fallback: if defaultOptionId doesn't match, select the first option
        dynamicParameters[property.name] = String(property.options[0].optionValue);
      }
    } else {
      dynamicParameters[property.name] = null;
    }
  };

  // Initialize dynamic parameters reactively when displayableProperties changes
  // This handles both initial load and any updates to lens properties
  // Use shallow watch on property names to avoid expensive deep comparisons
  const propertyNames = computed(() =>
    displayableProperties.value.map((p) => p.name).join(',')
  );

  watch(
    [displayableProperties, propertyNames],
    ([properties]) => {
      properties.forEach((property) => {
        initializePropertyDefault(property);
      });
    },
    { immediate: true }
  );

  // Track previous parameter values for current_params (to capture old values before v-model updates)
  const previousParams = ref<Record<string, string | null>>({ ...dynamicParameters });

  // Backward compatibility: Create refs for commonly used properties
  // These are computed from dynamicParameters for seamless compatibility
  const radius = computed({
    get: () => dynamicParameters.Radius || null,
    set: (val) => {
      dynamicParameters.Radius = val;
    }
  });
  const diameter = computed({
    get: () => dynamicParameters.Diameter || null,
    set: (val) => {
      dynamicParameters.Diameter = val;
    }
  });
  const power = computed({
    get: () => dynamicParameters.Power || null,
    set: (val) => {
      dynamicParameters.Power = val;
    }
  });
  const cylinder = computed({
    get: () => dynamicParameters.Cylinder || null,
    set: (val) => {
      dynamicParameters.Cylinder = val;
    }
  });
  const axis = computed({
    get: () => dynamicParameters.Axis || null,
    set: (val) => {
      dynamicParameters.Axis = val;
    }
  });

  const pack = ref<string | null>(null);
  const canBeOrdered = ref(true);
  const quantity = ref<number>(1);
  const reference = ref<string>('');
  const patient = ref<Patient | null>(null);
  const patientSearchText = ref('');
  const patientSuggestions = ref<ISuggestion[]>([]);
  const isPatientSearchLoading = ref(false);
  const parametersError = ref(false);
  const showPowerSelection = ref(false);
  const selectedPowerQuantities = ref<{ power: string; quantity: number }[]>([]);

  // AbortController for cancelling patient search requests
  let patientSearchController: AbortController | null = null;

  const isMultiPowerMode = computed(() => props.powerSelectionMode === 'multi');
  const isLensCare = computed(() => props.productCategory === PRODUCT_CATEGORY_LENS_CARE);

  const resetPatientState = () => {
    patient.value = null;
    patientSearchText.value = '';
    patientSuggestions.value = [];
    // Cancel any pending patient search
    if (patientSearchController) {
      patientSearchController.abort();
      patientSearchController = null;
    }
  };
  watch(
    () => props.organisationId.value,
    () => {
      resetPatientState();
    }
  );

  // Get options for a specific property by name

  // Generate options for all displayable properties
  const dynamicPropertyOptions = computed(() => {
    const optionsMap: Record<string, IOption[]> = {};
    displayableProperties.value.forEach((property) => {
      optionsMap[property.name] = convertPropertyToOptions(property, formatPower);
    });
    return optionsMap;
  });

  // Get property option with value and label for serialization
  const getPropertyOption = (name: string, value: string | null): PropertyOption | null => {
    if (!value) {
      return null;
    }

    const property = lensPropertiesSource.value.find((p) => p.name === name);
    if (!property) {
      return null;
    }

    const label = getOptionLabel(property, value);
    return {
      value,
      label
    };
  };

  /**
   * Check if property is boolean by examining options
   * Boolean properties have exactly 2 options with labels "true"/"false"
   */
  const isBooleanProperty = (propertyName: string, properties: LensProperty[]): boolean => {
    const property = properties.find((p) => p.name === propertyName);
    if (property?.options?.length !== 2) {
      return false;
    }
    const labels = property.options.map((opt) => opt.optionLabel.toLowerCase());
    return labels.includes('true') && labels.includes('false');
  };

  const getVariantByValue = (value: string | null): Variant | null => {
    if (!value || !variants.value) {
      return null;
    }

    // Fallback to publicIdentifier for backward compatibility with older data
    return variants.value.find((p) => p.code === value || p.publicIdentifier === value) || null;
  };

  // Backward compatibility: specific option getters
  const radiusOptions = computed(() => dynamicPropertyOptions.value.Radius || []);
  const diameterOptions = computed(() => dynamicPropertyOptions.value.Diameter || []);
  const powerOptions = computed(() => dynamicPropertyOptions.value.Power || []);
  const cylinderOptions = computed(() => dynamicPropertyOptions.value.Cylinder || []);
  const axisOptions = computed(() => dynamicPropertyOptions.value.Axis || []);
  const packOptions = computed(() => {
    const sorted = sortVariantsForPackOptions(variants.value);
    return sorted.map((variant) => ({
      label: formatVariantLabel(variant),
      // Use code as primary identifier, fallback to publicIdentifier
      value: variant.code ?? variant.publicIdentifier
    }));
  });
  const validateReference = (event: { target: { value: string } }) => {
    if (event && event.target && typeof event.target.value === 'string') {
      reference.value = event.target.value.replace(/[<>"'&\n\r]/g, '').slice(0, 30);
    }
  };

  const searchPatients = async () => {
    if (patientSearchText.value.length < 1) {
      patientSuggestions.value = [];
      return;
    }

    // Cancel previous request if still pending
    if (patientSearchController) {
      patientSearchController.abort();
    }

    // Create new AbortController for this request
    patientSearchController = new AbortController();
    const currentController = patientSearchController;

    isPatientSearchLoading.value = true;
    try {
      if (!patientService.value) {
        return;
      }

      const response = await patientService.value.index({
        params: {
          'filter[search]': patientSearchText.value,
          'page[size]': 10
        },
        signal: currentController.signal
      });

      // Check if this request was cancelled
      if (currentController.signal.aborted) {
        return;
      }

      let patients: Patient[] = [];
      const responseData = response.data as any;

      if (responseData && typeof responseData === 'object' && 'data' in responseData && 'meta' in responseData && Array.isArray(responseData.data)) {
        patients = (responseData as PaginatedResponse<Patient[]>).data;
      } else if (Array.isArray(responseData)) {
        patients = responseData as Patient[];
      }

      patientSuggestions.value = patients.map((p: Patient) => ({
        id: p.id,
        value: p.id,
        label: `${p.first_name} ${p.last_name} - ID: ${p.mrn}` || `${p.first_name} ${p.last_name}`
      }));
    } catch (e: any) {
      // Don't show error if request was aborted
      if (e.name === 'AbortError' || e.name === 'CanceledError') {
        return;
      }
      console.error('Failed to search patients:', e);
      patientSuggestions.value = [];
    } finally {
      isPatientSearchLoading.value = false;
    }
  };

  const debouncedPatientSearch = debounce(searchPatients, 500);
  const isPatientSetting = ref(false);

  const handlePatientChange = async (suggestion: ISuggestion | string) => {
    isPatientSetting.value = true;
    if (typeof suggestion === 'object' && suggestion !== null && 'value' in suggestion && 'label' in suggestion && suggestion.id) {
      try {
        if (patientService.value) {
          try {
            const patientData = await patientService.value.fetch(suggestion.id as string);
            patient.value = patientData;
          } catch (fetchError) {
            console.error('Error fetching patient details:', fetchError);
            patient.value = {
              id: suggestion.id as string,
              first_name: suggestion.label.split(' ')[0] || '',
              last_name: suggestion.label.split(' ').slice(1).join(' ') || '',
              date_of_birth: '',
              age: 0
            };
          }
        } else {
          patient.value = {
            id: suggestion.id as string,
            first_name: suggestion.label.split(' ')[0] || '',
            last_name: suggestion.label.split(' ').slice(1).join(' ') || '',
            date_of_birth: '',
            age: 0
          };
        }
      } catch (error) {
        console.error('Failed to process patient selection:', error);
        patient.value = {
          id: suggestion.id as string,
          first_name: suggestion.label.split(' ')[0] || '',
          last_name: suggestion.label.split(' ').slice(1).join(' ') || '',
          date_of_birth: '',
          age: 0
        };
      }

      patientSearchText.value = suggestion.label;
      patientSuggestions.value = [];
      isPatientSetting.value = false;
    } else if (typeof suggestion === 'string') {
      patientSearchText.value = suggestion;
      if (suggestion === '') {
        patient.value = null;
        patientSuggestions.value = [];
        isPatientSetting.value = false;
      } else {
        debouncedPatientSearch();
        isPatientSetting.value = false;
      }
    }
  };

  const isProcessing = ref(false);

  // Wait for calculation to be done before proceeding
  const waitForParametersToLoad = (): Promise<void> =>
    new Promise((resolve) => {
      if (!isLoadingParameters.value) {
        resolve();
        return;
      }

      const stopWatcher = watch(
        isLoadingParameters,
        (isLoading) => {
          if (!isLoading) {
            stopWatcher();
            resolve();
          }
        },
        { immediate: true }
      );
    });

  const handleAddToCart = async () => {
    if (isProcessing.value) {
      return;
    }
    isProcessing.value = true;
    // Wait for parameters to finish loading before proceeding
    await waitForParametersToLoad();

    let orderItem: OrderItem;
    const variantInfo = getVariantByValue(pack.value);
    const materialProperty = lensPropertiesSource.value?.find((p: LensProperty) => p.name === 'Material');
    const defaultMaterialOption = materialProperty?.options?.find((option) => option.id === materialProperty.defaultOptionId);
    const defaultMaterialCode = defaultMaterialOption?.optionValue;

    const baseProductOptions = {
      category: {
        name: props.productCategory,
        slug: props.productCategorySlug,
        displayName: props.productCategoryDisplayName || props.productCategory
      },
      variants: variants.value,
      powers: lensPropertiesSource.value?.find((p: LensProperty) => p.name === 'Power') || null,
      lensProperties: lensPropertiesSource.value || null,
      ...(!isLensCare.value && {
        ...(props.lensCode && {
          lensCode: props.lensCode,
          lensId: props.lensCode
        }),
        ...(defaultMaterialCode && { materialCode: defaultMaterialCode })
      })
    };

    const productOptions = props.image ? { ...baseProductOptions, image: props.image } : baseProductOptions;

    if (isMultiPowerMode.value) {
      const orderedEntries = displayableProperties.value
        .filter((property) => property.name !== 'Power')
        .map((property) => {
          const value = dynamicParameters[property.name];
          return [property.name, getPropertyOption(property.name, value)] as [string, PropertyOption | null];
        });

      const optionsObj = Object.fromEntries(orderedEntries);

      orderItem = {
        mlsId: props.mlsId,
        patientId: patient.value?.id || null,
        productName: props.productName,
        options: JSON.stringify(optionsObj),
        reference: reference.value || null,
        quantity: quantity.value,
        // Use code as primary identifier, fallback to publicIdentifier
        variantOptionValue: (variantInfo as Variant)?.code ?? variantInfo?.publicIdentifier ?? '',
        variantOptionLabel: formatVariantLabel(variantInfo),
        productOptions,
        isStock: true,
        powerQuantities: JSON.stringify(
          selectedPowerQuantities.value.map((power) => ({
            power: power.power,
            quantity: power.quantity
          }))
        )
      };
    } else if (isLensCare.value) {
      orderItem = {
        mlsId: props.mlsId,
        patientId: patient.value?.id || null,
        productName: props.productName,
        options: JSON.stringify({}),
        reference: reference.value || null,
        quantity: quantity.value,
        // Use code as primary identifier, fallback to publicIdentifier
        variantOptionValue: variantInfo?.code ?? variantInfo?.publicIdentifier ?? '',
        variantOptionLabel: formatVariantLabel(variantInfo),
        productOptions
      };
    } else {
      const orderedEntries = displayableProperties.value.map((property) => {
        const value = dynamicParameters[property.name];
        return [property.name, getPropertyOption(property.name, value)] as [string, PropertyOption | null];
      });

      const optionsObj = Object.fromEntries(orderedEntries);

      orderItem = {
        mlsId: props.mlsId,
        patientId: patient.value?.id || null,
        productName: props.productName,
        options: JSON.stringify(optionsObj),
        reference: reference.value || null,
        quantity: quantity.value,
        // Use code as primary identifier, fallback to publicIdentifier
        variantOptionValue: variantInfo?.code ?? variantInfo?.publicIdentifier ?? '',
        variantOptionLabel: formatVariantLabel(variantInfo),
        productOptions
      };
    }

    try {
      await webshopCartStore.addItem(orderItem);
      notificationStore.addSuccessNotification({
        title: t('platform.common.success'),
        label: t('platform.webshop.notifications.item-added'),
        action: {
          label: t('platform.webshop.button.view-cart'),
          onClick: () => {
            router.push({ name: 'webshop-cart' });
          }
        },
        autoDismiss: true,
        dismissAfter: 3000
      });
      resetFormFields();
    } catch (error) {
      notificationStore.addErrorNotification({
        title: t('platform.common.failure'),
        label: t('platform.webshop.notifications.item-add-failed')
      });
    } finally {
      isProcessing.value = false;
    }
  };

  const resetFormFields = (): void => {
    patient.value = null;
    patientSearchText.value = '';
    patientSuggestions.value = [];

    if (isLensCare.value && !isMultiPowerMode.value) {
      pack.value = null;
      quantity.value = 1;
      reference.value = '';
      userSelectedParams.value.clear();
      return;
    }

    // Reset dynamic parameters - clear all first, then reinitialize with defaults
    displayableProperties.value.forEach((property) => {
      // Clear the value first so initializePropertyDefault will set the default
      delete dynamicParameters[property.name];
    });
    // Reinitialize with defaults (Material will get defaultOptionId, single options auto-selected)
    displayableProperties.value.forEach((property) => {
      initializePropertyDefault(property);
    });

    pack.value = null;
    quantity.value = 1;
    reference.value = '';
    userSelectedParams.value.clear();

    if (isMultiPowerMode.value) {
      selectedPowerQuantities.value = [];
      showPowerSelection.value = false;
      props.powerSelectionRef?.value?.reset();
    }
  };

  /**
   * Map of property names to error strings for invalid properties.
   * Only shows errors for properties that:
   * 1. Have isValid === false from MLS API
   * 2. Were explicitly selected by the user (exist in userSelectedParams)
   * 3. Are NOT currently loading (to prevent error flashing during API calls)
   * Returns space string ' ' to trigger red border without displaying error text.
   */
  const invalidPropertyErrors = computed(() => {
    const errors: Record<string, string> = {};

    // Don't show errors while loading parameters to prevent error flashing
    // Loading state means data is being recalculated, errors would be based on stale data
    if (isLoadingParameters.value) {
      return errors;
    }

    lensPropertiesSource.value.forEach((property) => {
      // Only show error if property is invalid AND user has selected a value for it
      if (property.isValid === false && userSelectedParams.value.has(property.name)) {
        // Use space string to trigger red border without showing error message
        errors[property.name] = ' ';
      }
    });

    return errors;
  });

  /**
   * Check if there are any invalid properties that user has selected
   */
  const hasInvalidSelectedProperties = computed(() => lensPropertiesSource.value.some((property) =>
    property.isValid === false && userSelectedParams.value.has(property.name)
  ));

  const isAddToCartDisabled = computed(() => {
    const isInvalidQuantity = quantity.value < 1;

    if (!canBeOrdered.value || isProcessing.value || hasInvalidSelectedProperties.value) {
      return true;
    }

    if (isMultiPowerMode.value) {
      // Check all displayable properties (except Power) are filled
      const hasUnfilledParam = displayableProperties.value.some((property) => {
        if (property.name === 'Power') {
          return false;
        }
        const options = dynamicPropertyOptions.value[property.name] || [];
        return options.length > 0 && !dynamicParameters[property.name];
      });

      // Check pack is selected if options exist
      const hasPackOptions = packOptions.value.length > 0;
      const packUnfilled = hasPackOptions && !pack.value;

      return (
        hasUnfilledParam ||
        packUnfilled ||
        !selectedPowerQuantities.value.some((power) => power.quantity > 0)
      );
    }

    if (isLensCare.value) {
      const hasPackOptions = packOptions.value.length > 0;
      return (hasPackOptions && !pack.value) || isInvalidQuantity;
    }

    // Check all displayable properties are filled
    const hasUnfilledParam = displayableProperties.value.some((property) => {
      const options = dynamicPropertyOptions.value[property.name] || [];
      return options.length > 0 && !dynamicParameters[property.name];
    });

    // Check pack is selected if options exist
    const hasPackOptions = packOptions.value.length > 0;
    const packUnfilled = hasPackOptions && !pack.value;

    return isInvalidQuantity || hasUnfilledParam || packUnfilled;
  });

  // Properties that should send label instead of value to MLS API
  const LABEL_VALUE_PROPERTIES = ['Color'];

  /**
   * Get the label for a property value (used for properties like Color that need label sent to MLS)
   */
  const getPropertyLabelForValue = (propertyName: string, value: string | null): string | null => {
    if (!value) {
      return null;
    }
    const property = lensPropertiesSource.value.find((p) => p.name === propertyName);
    if (!property?.options) {
      return value;
    }
    const option = property.options.find((opt) => String(opt.optionValue) === value);
    return option ? option.optionLabel : value;
  };

  const MAX_AUTO_SELECT_DEPTH = 1;

  const calculateLensProperties = async (changedParams: ChangeParams, depth = 0) => {
    try {
      isLoadingParameters.value = true;
      parametersError.value = false;

      // Mark this parameter as explicitly selected by the user
      userSelectedParams.value.add(changedParams.propertyName);

      // Build current_params from previous snapshot (has old values before v-model update)
      // Filter out null values AND exclude LABEL_VALUE_PROPERTIES (like Color) unless user explicitly selected them
      // This prevents auto-filled lensFitting values (like Color) from being sent back to MLS
      const currentParams = Object.fromEntries(
        Object.entries(previousParams.value)
          .filter(([key, value]) => {
            if (value === null || value === '') {
              return false;
            }
            // Exclude params the user cleared without triggering an API call
            // (previousParams still has the stale value, but dynamicParameters reflects the clear)
            const currentValue = dynamicParameters[key];
            if (currentValue === null || currentValue === '') {
              return false;
            }
            // For Color and similar properties, only include if user explicitly selected them
            if (LABEL_VALUE_PROPERTIES.includes(key) && !userSelectedParams.value.has(key)) {
              return false;
            }
            return true;
          })
          .map(([key, value]) => {
            // For Color and similar properties, MLS expects the label (e.g., "Nudy Brown") not the index value
            if (LABEL_VALUE_PROPERTIES.includes(key) && value) {
              const label = getPropertyLabelForValue(key, value);
              return [key, label || value];
            }
            return [key, value];
          })
      ) as Record<string, string>;

      // For Color and similar properties, MLS expects the label (e.g., "Nudy Brown") not the index value
      const apiChangedParams = { ...changedParams };
      if (LABEL_VALUE_PROPERTIES.includes(changedParams.propertyName)) {
        const label = getPropertyLabelForValue(changedParams.propertyName, changedParams.value);
        if (label) {
          apiChangedParams.value = label;
        }
      }

      if (!webshopService.value) {
        throw new Error('Webshop service not initialized');
      }

      const response = await webshopService.value.calculateLens(props.mlsId, {
        current_params: currentParams,
        changed_params: apiChangedParams
      });

      canBeOrdered.value = response.data.canBeOrdered;
      dynamicParameters[changedParams.propertyName] = changedParams.value;
      variants.value = response.data.variants;

      // Update lensProperties from response
      if (response.data.lensProperties) {
        // Track previously displayable properties before update
        const previousPropertyNames = new Set(
          displayableProperties.value.map((p) => p.name)
        );

        internalLensProperties.value = response.data.lensProperties;

        // Get new displayable property names after update (must recompute displayableProperties)
        const newDisplayableProperties = getDisplayableLensProperties(response.data.lensProperties);
        const newPropertyNames = new Set(newDisplayableProperties.map((p) => p.name));

        // Keep invalid selections and mark them for error display
        // Instead of auto-clearing invalid values, we keep them and let the user actively select a valid option
        // The MLS API returns isValid: false for properties with invalid combinations
        // Uses isValueValidForProperty to handle both discrete options and step-based ranges (MTO lenses)
        response.data.lensProperties.forEach((property) => {
          const currentValue = dynamicParameters[property.name];

          const isChangingNow = property.name === changedParams.propertyName;
          const isAlreadyNull = currentValue === null || typeof currentValue === 'undefined';

          // Skip if we shouldn't validate this property right now
          if (isAlreadyNull || isChangingNow || isValueValidForProperty(property, currentValue)) {
            return;
          }

          // If we reach here, it's invalid - mark as user-selected so error will show
          userSelectedParams.value.add(property.name);
        });

        // Clear properties that were removed from response (no longer applicable)
        previousPropertyNames.forEach((propertyName) => {
          if (!newPropertyNames.has(propertyName) && propertyName !== changedParams.propertyName) {
            dynamicParameters[propertyName] = null;
          }
        });
      }

      // Apply lensFitting pre-filled values
      if (response.data.lensFitting) {
        const lensFittingUpdates = Object.entries(response.data.lensFitting)
          .map(([key, value]) => {
            // Convert camelCase backend key to PascalCase UI property name
            const pascalKey = key.charAt(0).toUpperCase() + key.slice(1);
            return { pascalKey, value };
          })
          .filter(({ pascalKey }) => {
            // Skip if no matching property in lensProperties
            const propertyExists = internalLensProperties.value.some((p) => p.name === pascalKey);
            if (!propertyExists) {
              return false;
            }

            // Skip changed param - preserve user selection
            if (pascalKey === changedParams.propertyName) {
              return false;
            }

            // Skip if user has explicitly selected this parameter (even if it's now invalid)
            // This preserves invalid user selections so they can be marked red and user can actively change them
            if (userSelectedParams.value.has(pascalKey)) {
              return false;
            }

            // Only apply if param not already selected (null) - don't overwrite existing selections
            if (dynamicParameters[pascalKey] !== null) {
              return false;
            }

            return true;
          })
          .map(({ pascalKey, value }) => {
            // Find the property to validate against available options or step range
            const property = internalLensProperties.value.find((p) => p.name === pascalKey);

            // Validate that the lensFitting value is actually available
            const valueToApply = isBooleanProperty(pascalKey, internalLensProperties.value)
              ? (value === 'true' ? '1' : '0')
              : String(value);

            // Use unified validation that handles both discrete options and step-based ranges
            const isValidOption = property ? isValueValidForProperty(property, valueToApply) : false;

            return { pascalKey, valueToApply, isValidOption };
          })
          .filter(({ isValidOption }) => isValidOption);

        // Apply all validated updates
        Object.assign(
          dynamicParameters,
          Object.fromEntries(lensFittingUpdates.map(({ pascalKey, valueToApply }) => [pascalKey, valueToApply]))
        );
      }

      // Detect properties narrowed to a single option that are not yet selected
      // and auto-select them, then make a follow-up MLS call to validate the full set
      const autoSelectedParams: ChangeParams[] = [];
      const updatedDisplayable = sortLensProperties(
        getDisplayableLensProperties(internalLensProperties.value)
      );

      for (const property of updatedDisplayable) {
        const currentValue = dynamicParameters[property.name];
        const isEmpty = currentValue === null || currentValue === '' || typeof currentValue === 'undefined';
        if (!isEmpty) {
          continue;
        }

        const options = convertPropertyToOptions(property, formatPower);
        if (options.length === 1) {
          dynamicParameters[property.name] = options[0].value;
          autoSelectedParams.push({ propertyName: property.name, value: options[0].value });
        }
      }

      if (autoSelectedParams.length > 0 && depth < MAX_AUTO_SELECT_DEPTH) {
        const lastAutoSelected = autoSelectedParams[autoSelectedParams.length - 1];
        previousParams.value = { ...dynamicParameters };
        await calculateLensProperties(lastAutoSelected, depth + 1);
        return;
      }

      if (props.powerSelectionMode === 'multi') {
        showPowerSelection.value = true;
      }

      // Update snapshot with current values for next API call
      // Note: Shallow copy is required because dynamicParameters is a reactive object
      // and we need to capture its current state, not maintain a reference to it
      previousParams.value = { ...dynamicParameters };
    } catch (err) {
      parametersError.value = true;
      notificationStore.addErrorNotification({
        title: t('platform.webshop.errors.fetch-error')
      });
    } finally {
      isLoadingParameters.value = false;
    }
  };

  return {
    // Backward compatibility - specific parameter refs
    radius,
    diameter,
    power,
    cylinder,
    axis,
    // Dynamic parameter system
    dynamicParameters,
    displayableProperties,
    dynamicPropertyOptions,
    // Common fields
    pack,
    quantity,
    reference,
    patient,
    patientSearchText,
    patientSuggestions,
    isPatientSearchLoading,
    isPatientSetting,
    showPowerSelection,
    selectedPowerQuantities,
    isLoadingParameters,
    canBeOrdered,
    parametersError,
    invalidPropertyErrors,
    // Backward compatibility - specific option getters
    radiusOptions,
    diameterOptions,
    powerOptions,
    cylinderOptions,
    axisOptions,
    packOptions,
    // Computed flags
    isMultiPowerMode,
    isLensCare,
    isAddToCartDisabled,
    // Methods
    getPropertyOption,
    getVariantByValue,
    handleAddToCart,
    resetFormFields,
    validateReference,
    handlePatientChange,
    searchPatients,
    debouncedPatientSearch,
    calculateLensProperties
  };
}
